import { columnType } from 'components/table/types';
import { SupportType, pendingRequestsTypes } from 'constants/requests.constants';
import { tableColumns } from './tables.types';
import _ from 'lodash';
import { Box, Button, Tooltip } from '@mui/material';
import { getCompanyNameByLanguage } from 'utils/index';
import { WorldCheckStatus, generalTranslation } from 'constants/global.constants';
import { TheatersOutlined } from '@mui/icons-material';
import { BalanceType } from 'constants/balance.constants';
import { formatNumber } from 'components/FormCreatorComponent/helpers';

export const tableData = (
	type: pendingRequestsTypes,
	data: Record<string, any>[],
	t: any,
	companies: Record<string, any>[] = []
) => {
	const companiesObj = _.keyBy(companies, 'id');
	const getCompanyName = (companyId?: number) => {
		const company = _.get(companiesObj, String(companyId));
		return getCompanyNameByLanguage(company) || '';
	};

	return data.map((item) => ({
		...item,
		createdBy: `${item?.createdBy?.firstNameHebrew || item?.createdBy?.firstName || ''} 
    ${item?.createdBy?.lastNameHebrew || item?.createdBy?.lastName || ''}`,

		requestedByContact: `${item?.requestedByContact?.firstName || ''} 
    ${item?.requestedByContact?.lastName || ''}`,

		hebrewName: `${item?.createdBy?.firstNameHebrew || ''} 
    ${item?.createdBy?.lastNameHebrew || ''}`,

		contactHebrewName: getCompanyName(item?.companyId || item?.requested_by_company_id),

		requestedName: `${item?.firstName || ''} 
    ${item?.lastName || ''}`,

		bankName: _.isEqual(type, pendingRequestsTypes.BALANCES)
			? item?.bankAccount?.bankName?.bankName || ''
			: item?.bankName?.bankName,

		accountNumber: _.isEqual(type, pendingRequestsTypes.BALANCES)
			? item?.bankAccount?.accountNumber || ''
			: item?.accountNumber,

		bankBranch: _.isEqual(type, pendingRequestsTypes.BALANCES)
			? item?.bankAccount?.bankBranch || ''
			: item?.bankBranch,

		currency: _.isEqual(type, pendingRequestsTypes.DIGITAL_BALANCES_REQUESTS)
			? item?.currency
			: [pendingRequestsTypes.PAYMENTS, pendingRequestsTypes.BALANCES_BO_REQUESTS].includes(type)
			? item?.currency?.symbol || ''
			: _.isEqual(type, pendingRequestsTypes.WITHDRAWAL_BO_APPROVED)
			? item?.activityBalanceAddition.currency?.symbol || ''
			: item?.currency?.name || '',
		currencyName: [pendingRequestsTypes.BALANCES_BO_REQUESTS, pendingRequestsTypes.WITHDRAWAL_SUPPORT].includes(
			type
		)
			? item?.currency?.name || item?.currency || ''
			: '',
		nickname: _.isEqual(type, pendingRequestsTypes.PAYMENTS) ? item?.beneficiary?.nickname || '' : item?.nickname,
		balanceType: _.isEqual(type, pendingRequestsTypes.BALANCES_BO_REQUESTS)
			? _.get(generalTranslation as object, item.type)
			: item.type,
		companyName: _.isEqual(type, pendingRequestsTypes.PAYMENTS)
			? item?.beneficiary?.companyName || ''
			: _.isEqual(type, pendingRequestsTypes.DIGITAL_BALANCES_REQUESTS)
			? getCompanyName(item?.companyId)
			: item?.companyName,

		paymentType: item?.type,

		fee: item?.fee?.swiftFeeType,

		subjectWithdrawal: _.isEqual(item?.type, SupportType.WithdrawalWithClose)
			? t('actions.name.yes')
			: t('actions.name.no'),

		status: _.isEqual(type, pendingRequestsTypes.WITHDRAWAL_BO_APPROVED)
			? generalTranslation[item?.activityBalanceAddition?.status]
			: item?.status === 'SignedPendingBOApproval'
			? 'Pending'
			: item?.status,

		wcHebrewStatus: WorldCheckStatus[item?.wcStatus],
		typeHebrew: generalTranslation[item?.type],
		countryAtRiskDuringCreation: _.isNull(item?.countryAtRiskDuringCreation)
			? item?.country?.isRisky
			: item?.countryAtRiskDuringCreation,
		bankCountryAtRisk: _.get(item, 'bankAccountCountry.isRisky'),
		countryIsTaxShelter: _.get(item, 'country.isTaxShelter'),

		firstSigner: !_.isEmpty(_.get(item, 'boApprovedBySecondarySignerUser'))
			? _.get(item, 'boApprovedBySecondarySignerUser.firstName') +
			  ' ' +
			  _.get(item, 'boApprovedBySecondarySignerUser.lastName')
			: '-',
		secondSigner: !_.isEmpty(_.get(item, 'boApprovedByMainSignerUser'))
			? _.get(item, 'boApprovedByMainSignerUser.firstName') +
			  ' ' +
			  _.get(item, 'boApprovedByMainSignerUser.lastName')
			: '-',
		amount: _.isEqual(type, pendingRequestsTypes.WITHDRAWAL_SUPPORT) && item.amount === 0 ? '' : item.amount,

		// Digital balance fields
		...(_.isEqual(type, pendingRequestsTypes.DIGITAL_BALANCES_REQUESTS) && {
			reference: item?.reference || '',
			clientBankAccount: `${item?.bankAccount?.bankName?.bankName || ''} , ${item?.bankAccount?.bankBranch || ''}, ${item?.bankAccount?.accountNumber || ''}`,
			altshulerBankAccount: `${item?.altshulerAccount?.bank?.bankName || ''} , ${item?.altshulerAccount?.bankBranch || ''}, ${item?.altshulerAccount?.accountNumber || ''}`,
			hyperAccountNumber: item?.company?.hyperAccountNumber || '',
			documentedOnHyperAction: item?.documentedOnHyper,
			documentedOnHyperDate: item?.hyperDocumentedAt,
			documentedByBoUser: item?.documentedByBoUser
				? `${item.documentedByBoUser.firstName || ''} ${item.documentedByBoUser.lastName || ''}`.trim()
				: '',
			feezbackBankReferenceNumber: item?.feezbackBankReferenceNumber || '',
			feezbackUpdatedAt: item?.feezbackUpdatedAt || '',
		}),
	}));
};

// export enum withdrawalOptions {
// 	REGULAR_WITHDRAWAL = 'regularWithdrawal',
// 	CLOSE_WITHDRAWAL = 'closeWithdrawal',
// }

export const buildColumns = (
	type: pendingRequestsTypes,
	t: any,
	sortableColumns?: string[],
	onDocumentedActionClick?: (requestId: number) => void
): columnType[] => {
	const columns: columnType[] = [];

	if (_.has(tableColumns, type)) {
		_.keys(tableColumns[type]).forEach((element) => {
			let customValueHandle = {};
			if (
				element === 'amount' &&
				[
					pendingRequestsTypes.PAYMENTS,
					pendingRequestsTypes.WITHDRAWAL_BO_APPROVED,
					pendingRequestsTypes.BALANCES,
					pendingRequestsTypes.BALANCES_BO_REQUESTS,
					pendingRequestsTypes.DIGITAL_BALANCES_REQUESTS,
				].includes(type)
			) {
				customValueHandle = {
					customValueHandle: (value: any, row: Record<string, any>) => {
						if (type === pendingRequestsTypes.DIGITAL_BALANCES_REQUESTS) {
							return value ? `${formatNumber(value)} ${row?.currency?.symbol || ''}` : '';
						}
						if (type === pendingRequestsTypes.WITHDRAWAL_BO_APPROVED) {
							return `${row?.currency || ''}${row?.activityBalanceAddition.amount?.toLocaleString()}`;
						} else if (type === pendingRequestsTypes.BALANCES) {
							return value?.toLocaleString();
						} else if (type === pendingRequestsTypes.BALANCES_BO_REQUESTS) {
							return `${_.get(row, 'currency')}${value?.toLocaleString()}`;
						}
						return `${row?.currency || ''}${value?.toLocaleString()}`;
					},
				};
			}
			if (
				element === 'documentedOnHyperAction' &&
				type === pendingRequestsTypes.DIGITAL_BALANCES_REQUESTS &&
				onDocumentedActionClick
			) {
				customValueHandle = {
					customValueHandle: (value: any, row: Record<string, any>) => {
						if (!value) {
							return (
								<Button
									variant="contained"
									color="primary"
									size="small"
									onClick={() => onDocumentedActionClick(row.id)}
								>
									{t('page.pendingRequests.field.approve.documentedOnHyper')}
								</Button>
							);
						}
						return <span />;
					},
				};
			}
			if (element === 'paymentType' && type === pendingRequestsTypes.PAYMENTS) {
				customValueHandle = {
					customValueHandle: (value: any, row: Record<string, any>) => {
						return (
							<Box position={'relative'}>
								{row?.fee && (
									<Tooltip title={row.fee}>
										<img
											style={{
												position: 'absolute',
												left: '100%',
											}}
											src="/icons/swift.svg"
										/>
									</Tooltip>
								)}
								<Box>{value}</Box>
							</Box>
						);
					},
				};
			}
			if (
				[
					'countryAtRiskDuringCreation',
					'bankCountryAtRisk',
					'countryIsTaxShelter',
					'bankCountryIsTaxShelter',
				].includes(element)
			) {
				customValueHandle = {
					customValueHandle: (value: any, row: Record<string, any>) => {
						return value ? t('label.yes') : t('label.no');
					},
				};
			}
			const props: columnType = {
				fieldId: 'id',
				name: element,
				title: t('page.pendingRequests.field.' + element),
				type: tableColumns[type]?.[element] ?? 'string',
				sortable: sortableColumns ? sortableColumns.includes(element) : true,
				...customValueHandle,
			};
			columns.push(props);
		});
	}
	return columns;
};
